"use strict";
/*
  The MIT License

  Copyright (c) 2017-2019 EclipseSource Munich
  https://github.com/eclipsesource/jsonforms

  Permission is hereby granted, free of charge, to any person obtaining a copy
  of this software and associated documentation files (the "Software"), to deal
  in the Software without restriction, including without limitation the rights
  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
  copies of the Software, and to permit persons to whom the Software is
  furnished to do so, subject to the following conditions:

  The above copyright notice and this permission notice shall be included in
  all copies or substantial portions of the Software.

  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
  THE SOFTWARE.
*/
Object.defineProperty(exports, "__esModule", { value: true });
exports.isInherentlyEnabled = exports.isEnabled = exports.isVisible = exports.hasEnableRule = exports.hasShowRule = exports.evalEnablement = exports.evalVisibility = void 0;
var tslib_1 = require("tslib");
var has_1 = tslib_1.__importDefault(require("lodash/has"));
var models_1 = require("../models");
var resolvers_1 = require("./resolvers");
var path_1 = require("./path");
var reducers_1 = require("../reducers");
var isOrCondition = function (condition) {
    return condition.type === 'OR';
};
var isAndCondition = function (condition) {
    return condition.type === 'AND';
};
var isLeafCondition = function (condition) {
    return condition.type === 'LEAF';
};
var isSchemaCondition = function (condition) { return has_1.default(condition, 'schema'); };
var getConditionScope = function (condition, path) {
    return path_1.composeWithUi(condition, path);
};
var evaluateCondition = function (data, condition, path, ajv) {
    if (isAndCondition(condition)) {
        return condition.conditions.reduce(function (acc, cur) { return acc && evaluateCondition(data, cur, path, ajv); }, true);
    }
    else if (isOrCondition(condition)) {
        return condition.conditions.reduce(function (acc, cur) { return acc || evaluateCondition(data, cur, path, ajv); }, false);
    }
    else if (isLeafCondition(condition)) {
        var value = resolvers_1.resolveData(data, getConditionScope(condition, path));
        return value === condition.expectedValue;
    }
    else if (isSchemaCondition(condition)) {
        var value = resolvers_1.resolveData(data, getConditionScope(condition, path));
        return ajv.validate(condition.schema, value);
    }
    else {
        // unknown condition
        return true;
    }
};
var isRuleFulfilled = function (uischema, data, path, ajv) {
    var condition = uischema.rule.condition;
    return evaluateCondition(data, condition, path, ajv);
};
var evalVisibility = function (uischema, data, path, ajv) {
    if (path === void 0) { path = undefined; }
    var fulfilled = isRuleFulfilled(uischema, data, path, ajv);
    switch (uischema.rule.effect) {
        case models_1.RuleEffect.HIDE:
            return !fulfilled;
        case models_1.RuleEffect.SHOW:
            return fulfilled;
        // visible by default
        default:
            return true;
    }
};
exports.evalVisibility = evalVisibility;
var evalEnablement = function (uischema, data, path, ajv) {
    if (path === void 0) { path = undefined; }
    var fulfilled = isRuleFulfilled(uischema, data, path, ajv);
    switch (uischema.rule.effect) {
        case models_1.RuleEffect.DISABLE:
            return !fulfilled;
        case models_1.RuleEffect.ENABLE:
            return fulfilled;
        // enabled by default
        default:
            return true;
    }
};
exports.evalEnablement = evalEnablement;
var hasShowRule = function (uischema) {
    if (uischema.rule &&
        (uischema.rule.effect === models_1.RuleEffect.SHOW ||
            uischema.rule.effect === models_1.RuleEffect.HIDE)) {
        return true;
    }
    return false;
};
exports.hasShowRule = hasShowRule;
var hasEnableRule = function (uischema) {
    if (uischema.rule &&
        (uischema.rule.effect === models_1.RuleEffect.ENABLE ||
            uischema.rule.effect === models_1.RuleEffect.DISABLE)) {
        return true;
    }
    return false;
};
exports.hasEnableRule = hasEnableRule;
var isVisible = function (uischema, data, path, ajv) {
    if (path === void 0) { path = undefined; }
    if (uischema.rule) {
        return exports.evalVisibility(uischema, data, path, ajv);
    }
    return true;
};
exports.isVisible = isVisible;
var isEnabled = function (uischema, data, path, ajv) {
    if (path === void 0) { path = undefined; }
    if (uischema.rule) {
        return exports.evalEnablement(uischema, data, path, ajv);
    }
    return true;
};
exports.isEnabled = isEnabled;
/**
 * Indicates whether the given `uischema` element shall be enabled or disabled.
 * Checks the global readonly flag, uischema rule, uischema options (including the config),
 * the schema and the enablement indicator of the parent.
 */
var isInherentlyEnabled = function (state, ownProps, uischema, schema, rootData, config) {
    var _a, _b, _c;
    if ((_a = state === null || state === void 0 ? void 0 : state.jsonforms) === null || _a === void 0 ? void 0 : _a.readonly) {
        return false;
    }
    if (uischema && exports.hasEnableRule(uischema)) {
        return exports.isEnabled(uischema, rootData, ownProps === null || ownProps === void 0 ? void 0 : ownProps.path, reducers_1.getAjv(state));
    }
    if (typeof ((_b = uischema === null || uischema === void 0 ? void 0 : uischema.options) === null || _b === void 0 ? void 0 : _b.readonly) === 'boolean') {
        return !uischema.options.readonly;
    }
    if (typeof ((_c = uischema === null || uischema === void 0 ? void 0 : uischema.options) === null || _c === void 0 ? void 0 : _c.readOnly) === 'boolean') {
        return !uischema.options.readOnly;
    }
    if (typeof (config === null || config === void 0 ? void 0 : config.readonly) === 'boolean') {
        return !config.readonly;
    }
    if (typeof (config === null || config === void 0 ? void 0 : config.readOnly) === 'boolean') {
        return !config.readOnly;
    }
    if ((schema === null || schema === void 0 ? void 0 : schema.readOnly) === true) {
        return false;
    }
    if (typeof (ownProps === null || ownProps === void 0 ? void 0 : ownProps.enabled) === 'boolean') {
        return ownProps.enabled;
    }
    return true;
};
exports.isInherentlyEnabled = isInherentlyEnabled;
//# sourceMappingURL=runtime.js.map